<template>
  <v-dialog v-model="dialogModel" max-width="640" persistent>
    <v-card class="alert-feedback-dialog">
      <v-card-title class="subtitle-1 font-weight-bold d-flex align-center">
        <v-icon left small>mdi-clipboard-check-outline</v-icon>
        <span>{{ titleText }}</span>
        <v-spacer />
        <v-btn icon small @click="cancel"><v-icon small>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider />

      <v-card-text class="alert-feedback-dialog__body py-3">
        <!-- Alert-by-alert tri-state list (idea_alert + multi-gesture cards) -->
        <div v-if="(mode === 'idea_alert' || mode === 'gesture') && alerts.length" class="mb-3">
          <div class="caption text--secondary mb-2">
            Mark each alert as correct or incorrect. Alerts left unmarked are skipped.
          </div>
          <div
            v-for="(alert, idx) in alerts"
            :key="alert.id || idx"
            class="alert-feedback-row"
          >
            <div class="alert-feedback-row__header d-flex align-center py-1">
              <v-btn
                icon
                x-small
                class="mr-1"
                :aria-label="expanded[alert.id] ? 'Collapse alert details' : 'Expand alert details'"
                @click="toggleExpanded(alert.id)"
              >
                <v-icon small>{{ expanded[alert.id] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
              </v-btn>
              <div class="flex-grow-1 alert-feedback-row__main" @click="toggleExpanded(alert.id)">
                <div class="body-2 font-weight-medium">{{ alertLabel(alert) }}</div>
                <div class="caption text--secondary">{{ alertSubtitle(alert) }}</div>
              </div>
              <div class="alert-feedback-actions">
                <v-btn
                  small
                  depressed
                  :color="alertStates[alert.id] === 'correct' ? 'success' : 'grey lighten-3'"
                  :class="['alert-vote-btn', { 'alert-vote-btn--active': alertStates[alert.id] === 'correct' }]"
                  :aria-label="`Mark ${alertLabel(alert)} as correct`"
                  @click="setAlertVerdict(alert.id, 'correct')"
                >
                  <v-icon small :color="alertStates[alert.id] === 'correct' ? 'white' : 'grey darken-1'">mdi-check</v-icon>
                </v-btn>
                <v-btn
                  small
                  depressed
                  :color="alertStates[alert.id] === 'incorrect' ? 'error' : 'grey lighten-3'"
                  :class="['alert-vote-btn', { 'alert-vote-btn--active': alertStates[alert.id] === 'incorrect' }]"
                  :aria-label="`Mark ${alertLabel(alert)} as incorrect`"
                  @click="setAlertVerdict(alert.id, 'incorrect')"
                >
                  <v-icon small :color="alertStates[alert.id] === 'incorrect' ? 'white' : 'grey darken-1'">mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
            <v-expand-transition>
              <div v-if="expanded[alert.id]" class="alert-feedback-row__details">
                <dl class="alert-detail-grid">
                  <template v-for="(row, i) in alertDetailRows(alert)">
                    <dt :key="`dt-${alert.id}-${i}`">{{ row.label }}</dt>
                    <dd :key="`dd-${alert.id}-${i}`">{{ row.value }}</dd>
                  </template>
                </dl>
              </div>
            </v-expand-transition>
          </div>
        </div>

        <!-- Single target (idea_verdict, or gesture card with no sub-alerts) -->
        <div v-else-if="mode === 'idea_verdict' || (mode === 'gesture' && !alerts.length)" class="mb-3">
          <div class="d-flex align-center">
            <div class="flex-grow-1" style="min-width: 0;">
              <div class="body-2 font-weight-medium">{{ singleTargetLabel }}</div>
              <div class="caption text--secondary">{{ singleTargetSubtitle }}</div>
            </div>
            <div v-if="mode === 'gesture'" class="alert-feedback-actions ml-2">
              <v-btn
                small
                depressed
                :color="singleVerdict === 'correct' ? 'success' : 'grey lighten-3'"
                :class="['alert-vote-btn', { 'alert-vote-btn--active': singleVerdict === 'correct' }]"
                :aria-label="`Mark ${singleTargetLabel} as correct`"
                @click="setSingleVerdict('correct')"
              >
                <v-icon small :color="singleVerdict === 'correct' ? 'white' : 'grey darken-1'">mdi-check</v-icon>
              </v-btn>
              <v-btn
                small
                depressed
                :color="singleVerdict === 'incorrect' ? 'error' : 'grey lighten-3'"
                :class="['alert-vote-btn', { 'alert-vote-btn--active': singleVerdict === 'incorrect' }]"
                :aria-label="`Mark ${singleTargetLabel} as incorrect`"
                @click="setSingleVerdict('incorrect')"
              >
                <v-icon small :color="singleVerdict === 'incorrect' ? 'white' : 'grey darken-1'">mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <v-divider v-if="mode !== 'missing_gesture'" class="my-3" />

        <!-- Error type -->
        <div class="mb-2 subtitle-2">What kind of error?</div>
        <v-radio-group v-model="errorType" dense hide-details mandatory>
          <v-radio label="Over-detected" value="over_detected" />
          <v-radio label="Under-detected" value="under_detected" />
          <v-radio label="Wrong label" value="wrong_label" />
          <v-radio label="Wrong timing" value="wrong_timing" />
          <v-radio label="Other" value="other" />
        </v-radio-group>

        <v-divider class="my-3" />

        <!-- Cause -->
        <div class="mb-2 subtitle-2">Is it a technical or methodological issue?</div>
        <v-radio-group v-model="cause" dense hide-details mandatory row>
          <v-radio label="Technical" value="technical" />
          <v-radio label="Methodological" value="methodological" />
        </v-radio-group>

        <!-- Pre-filled timestamp (when triggered from "Report" missing-gesture flow) -->
        <div v-if="mode === 'missing_gesture' && missingTimestampMs != null" class="missing-pinned-block mb-3 d-flex align-center">
          <v-icon small color="warning" class="mr-2">mdi-flag</v-icon>
          <span class="body-2">Reporting a missing gesture at <strong>{{ formatMs(missingTimestampMs) }}</strong></span>
        </div>

        <!-- Which gesture was missed: without it the row cannot count towards per-gesture recall -->
        <v-select
          v-if="mode === 'missing_gesture'"
          v-model="missingGestureName"
          :items="gestureOptions"
          label="Missed gesture"
          hint="Required: which gesture should have been detected"
          persistent-hint
          outlined
          dense
          class="mb-3"
        />

        <v-divider class="my-3" />

        <v-textarea
          v-model="note"
          label="Note (optional)"
          rows="2"
          outlined
          dense
          hide-details
        />
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-4 py-2">
        <v-spacer />
        <v-btn text small @click="cancel">Cancel</v-btn>
        <v-btn
          color="primary"
          small
          depressed
          :loading="saving"
          :disabled="!canSubmit"
          @click="submit"
        >
          Submit feedback
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import DetectionFeedbackService from '../../services/DetectionFeedbackService';
import { GESTURE_NAME_TO_LABEL_EN } from '../../utils/gestureLabels';

export default {
  name: 'AlertFeedbackDialog',
  props: {
    value: { type: Boolean, default: false },
    mode: { type: String, default: 'idea_alert' }, // idea_alert | gesture | idea_verdict
    sourceType: { type: String, required: true },
    sourceId: { type: [Number, String], required: true },
    ideaId: { type: String, default: null },
    alerts: { type: Array, default: () => [] },
    target: { type: Object, default: null }, // for gesture / verdict modes
    /** When set, the dialog reports a missing-gesture observation at this timestamp. */
    missingTimestampMs: { type: Number, default: null },
  },
  data() {
    return {
      alertStates: {}, // { alertId: 'correct'|'incorrect' }
      expanded: {},    // { alertId: true }
      errorType: 'over_detected',
      cause: 'technical',
      note: '',
      saving: false,
      // Verdict for the single-target gesture flow (mode='gesture' && !alerts.length).
      // Null at start: the analyst must explicitly pick correct or incorrect so the
      // dialog doesn't carry state across different alerts.
      singleVerdict: null,
      // Gesture picked in missing_gesture mode; persisted as gesture_name.
      missingGestureName: null,
    };
  },
  computed: {
    /** Gesture catalogue for the missing-gesture picker, from the shared labels map. */
    gestureOptions() {
      return Object.entries(GESTURE_NAME_TO_LABEL_EN)
        .map(([value, text]) => ({ value, text }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    dialogModel: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); },
    },
    titleText() {
      if (this.mode === 'missing_gesture') return 'Report missing gesture';
      if (this.mode === 'gesture') return 'Report incorrect gesture';
      if (this.mode === 'idea_verdict') return 'Report incorrect idea';
      return 'Review idea alerts';
    },
    singleTargetLabel() {
      if (this.mode === 'gesture') return this.target?.label || this.target?.rule_id || 'Gesto';
      return this.target?.label || 'Idea';
    },
    singleTargetSubtitle() {
      if (this.mode === 'gesture') return `@ ${this.formatMs(this.target?.timestamp_ms)}`;
      return '';
    },
    /**
     * Whether the form has enough info to submit.
     * - idea_alert: at least one alert marked + (no missing flow yet so just states)
     * - gesture with sub-alerts: at least one sub-alert marked
     * - gesture single target: always (the verdict has a default)
     * - missing_gesture: a gesture must be picked (per-gesture recall needs the name)
     * - idea_verdict: always
     */
    canSubmit() {
      if (this.mode === 'missing_gesture') {
        return !!this.missingGestureName;
      }
      // When the dialog lists multiple alerts, require at least one verdict.
      if (this.mode === 'idea_alert' && this.alerts.length) {
        return Object.values(this.alertStates).some((v) => v === 'correct' || v === 'incorrect');
      }
      if (this.mode === 'gesture' && this.alerts.length) {
        return Object.values(this.alertStates).some((v) => v === 'correct' || v === 'incorrect');
      }
      // Single-target gesture: require the analyst to explicitly pick.
      if (this.mode === 'gesture' && !this.alerts.length) {
        return this.singleVerdict === 'correct' || this.singleVerdict === 'incorrect';
      }
      return true;
    },
  },
  watch: {
    value(open) {
      if (open) this.resetForm();
    },
  },
  methods: {
    resetForm() {
      this.alertStates = {};
      this.expanded = {};
      this.errorType = this.mode === 'missing_gesture' ? 'under_detected' : 'over_detected';
      this.cause = 'technical';
      this.note = '';
      this.singleVerdict = null;
      this.missingGestureName = null;
    },
    setSingleVerdict(verdict) {
      this.singleVerdict = verdict;
    },
    toggleExpanded(alertId) {
      if (this.expanded[alertId]) this.$delete(this.expanded, alertId);
      else this.$set(this.expanded, alertId, true);
    },
    alertDetailRows(alert) {
      const fmtBool = (v) => (v === true ? 'Yes' : v === false ? 'No' : '—');
      const fmtPct = (v) => (Number.isFinite(Number(v)) ? `${Math.round(Number(v) * 100)}%` : '—');
      const explain = alert?.explain || {};
      const rows = [
        { label: 'Rule ID', value: alert?.rule_id ?? '—' },
        { label: 'Gesture', value: alert?.gesture_name ?? alert?.name ?? '—' },
        { label: 'Timestamp', value: this.formatMs(alert?.timestamp_ms ?? alert?.gesture_timestamp_ms) },
        { label: 'Subtraction', value: alert?.subtraction != null ? `−${Math.round(Math.abs(alert.subtraction))}%` : '—' },
        { label: 'Confidence', value: fmtPct(explain.confidence ?? alert?.confidence) },
        { label: 'In baseline', value: fmtBool(explain.in_baseline) },
        { label: 'Overlaps subject speech', value: fmtBool(explain.overlaps_subject_speech) },
        { label: 'Overlaps interviewer speech', value: fmtBool(explain.overlaps_interviewer_speech) },
        { label: 'Suppressed', value: explain.suppressed_reason || '—' },
      ];
      if (explain.feature_weights && Object.keys(explain.feature_weights).length) {
        const weights = Object.entries(explain.feature_weights)
          .map(([k, v]) => `${k}=${Number(v).toFixed(2)}`)
          .join(', ');
        rows.push({ label: 'Feature weights', value: weights });
      }
      if (explain.notes) rows.push({ label: 'Notes', value: explain.notes });
      return rows;
    },
    setAlertVerdict(alertId, verdict) {
      const current = this.alertStates[alertId];
      if (current === verdict) {
        this.$delete(this.alertStates, alertId);
      } else {
        this.$set(this.alertStates, alertId, verdict);
      }
    },
    alertLabel(alert) {
      // No rule title (2026-10-28): the gesture names the alert.
      return alert?.gesture_name || alert?.name || alert?.rule_id || `Alert ${alert?.id ?? ''}`;
    },
    alertSubtitle(alert) {
      const ms = alert?.timestamp_ms ?? alert?.gesture_timestamp_ms;
      const sub = alert?.subtraction;
      const parts = [];
      if (ms != null) parts.push(`@ ${this.formatMs(ms)}`);
      if (sub != null) parts.push(`−${Math.round(Math.abs(sub))}%`);
      return parts.join(' · ');
    },
    /**
     * Durable time coordinate of an alert/target row. Falls back to a point at the
     * timestamp when the row carries no explicit range.
     */
    timeCoordinate(row) {
      const num = (v) => (v != null && Number.isFinite(Number(v)) ? Math.round(Number(v)) : null);
      const ts = num(row?.timestamp_ms ?? row?.gesture_timestamp_ms);
      const startMs = num(row?.start_ms) ?? ts;
      const endMs = num(row?.end_ms) ?? ts;
      return { startMs, endMs };
    },
    formatMs(ms) {
      if (ms == null || !Number.isFinite(Number(ms))) return '—';
      const total = Math.round(Number(ms) / 1000);
      const m = Math.floor(total / 60);
      const s = total % 60;
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    },
    async submit() {
      this.saving = true;
      try {
        const base = {
          sourceType: this.sourceType,
          sourceId: Number(this.sourceId),
          cause: this.cause,
          note: this.note || null,
        };
        const tasks = [];

        if (this.mode === 'idea_alert') {
          for (const alert of this.alerts) {
            const verdict = this.alertStates[alert.id];
            if (!verdict) continue;
            tasks.push(
              DetectionFeedbackService.submitAlertFeedback({
                ...base,
                ideaId: this.ideaId,
                alertId: String(alert.id),
                gestureName: alert.gesture_name || null,
                ...this.timeCoordinate(alert),
                verdict,
                errorType: verdict === 'incorrect' ? this.errorType : null,
              })
            );
          }
        } else if (this.mode === 'missing_gesture') {
          tasks.push(
            DetectionFeedbackService.submitMissingGesture({
              ...base,
              gestureName: this.missingGestureName,
              expectedTimestampMs: Number(this.missingTimestampMs),
            })
          );
        } else if (this.mode === 'gesture' && this.alerts.length) {
          // Multi-gesture card: one feedback row per sub-alert the user marked.
          for (const alert of this.alerts) {
            const verdict = this.alertStates[alert.id];
            if (!verdict) continue;
            tasks.push(
              DetectionFeedbackService.submitGestureFeedback({
                ...base,
                gestureId: String(alert.id),
                gestureName: alert.gesture_name || null,
                ...this.timeCoordinate(alert),
                verdict,
                errorType: verdict === 'incorrect' ? this.errorType : null,
              })
            );
          }
        } else if (this.mode === 'gesture') {
          const verdict = this.singleVerdict || 'incorrect';
          tasks.push(
            DetectionFeedbackService.submitGestureFeedback({
              ...base,
              gestureId: String(this.target?.id ?? this.target?.gesture_id),
              gestureName: this.target?.gesture_name || null,
              ...this.timeCoordinate(this.target),
              verdict,
              errorType: verdict === 'incorrect' ? this.errorType : null,
            })
          );
        } else if (this.mode === 'idea_verdict') {
          tasks.push(
            DetectionFeedbackService.submitIdeaVerdictFeedback({
              ...base,
              ideaId: this.ideaId,
              verdict: 'incorrect',
            })
          );
        }

        await Promise.all(tasks);
        // Emit a payload with the verdicts the user just persisted so parents
        // can patch their local state without waiting for a backend re-fetch.
        const verdicts = { ...this.alertStates };
        if (this.mode === 'gesture' && !this.alerts.length && this.singleVerdict) {
          verdicts[String(this.target?.id ?? this.target?.gesture_id)] = this.singleVerdict;
        }
        this.$emit('saved', { mode: this.mode, verdicts });
        this.dialogModel = false;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[AlertFeedbackDialog] submit failed', e);
      } finally {
        this.saving = false;
      }
    },
    cancel() {
      this.dialogModel = false;
      this.$emit('cancel');
    },
  },
};
</script>

<style scoped>
.alert-feedback-row + .alert-feedback-row {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.alert-feedback-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.alert-vote-btn {
  min-width: 36px !important;
  height: 32px !important;
  padding: 0 8px !important;
  border-radius: 6px !important;
  transition: transform 0.1s ease;
}

.alert-vote-btn--active {
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.04), 0 2px 6px rgba(0, 0, 0, 0.12) !important;
  transform: scale(1.05);
}

.alert-feedback-row__main {
  cursor: pointer;
  min-width: 0;
}

.alert-feedback-row__details {
  padding: 8px 12px 12px 36px;
  background: #fafafa;
  border-radius: 6px;
  margin: 4px 0 8px;
}

.alert-detail-grid {
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 12px;
  row-gap: 4px;
  margin: 0;
  font-size: 12px;
}

.alert-detail-grid dt {
  font-weight: 600;
  color: #555;
}

.alert-detail-grid dd {
  margin: 0;
  color: #212121;
  word-break: break-word;
}

.missing-pinned-block {
  background: #fff8e1;
  border: 1px solid #ffd54f;
  border-radius: 8px;
  padding: 10px 12px;
}
</style>
